import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createRunnerDispatchPolicy,
  createRunnerDispatchPolicyPreset,
  deleteRunnerDispatchPolicy,
  deleteRunnerDispatchPolicyPreset,
  fetchRunnerDispatchPolicies,
  fetchRunnerDispatchPoliciesWithRecommendation,
  reorderRunnerDispatchPolicies,
  updateRunnerDispatchPolicy,
  updateRunnerDispatchPolicyPreset,
  type CreateRunnerDispatchPolicyInput,
  type CreateRunnerDispatchPolicyPresetInput,
  type RunnerDispatchPolicyEntityType,
  type UpdateRunnerDispatchPolicyInput,
  type UpdateRunnerDispatchPolicyPresetInput,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

export const useRunnerDispatchPolicies = (
  token: string | null,
  projectId: string,
  params?: { entityType?: RunnerDispatchPolicyEntityType; entityState?: string },
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.runnerDispatchPolicies.list(projectId, params),
    queryFn: () => fetchRunnerDispatchPolicies(token as string, projectId, params),
    enabled: enabled && Boolean(token) && projectId.length > 0,
    staleTime: 60_000,
    gcTime: 1_800_000,
  });

/**
 * 러너 요청 컨텍스트용 정책 조회. entityId를 함께 보내 서버 추천(코드리뷰 OPEN)을 받는다.
 * 추천은 초기 선택 제안일 뿐이며, 적용 여부는 호출 측의 선택 출처(AUTO|USER) 규칙이 결정한다.
 */
export const useRunnerDispatchPoliciesWithRecommendation = (
  token: string | null,
  projectId: string,
  params: { entityType: RunnerDispatchPolicyEntityType; entityState: string; entityId?: string } | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: queryKeys.runnerDispatchPolicies.list(projectId, params),
    queryFn: () => fetchRunnerDispatchPoliciesWithRecommendation(token as string, projectId, params!),
    enabled: enabled && Boolean(token) && projectId.length > 0 && params !== undefined,
    staleTime: 60_000,
    gcTime: 1_800_000,
    // 정책은 엔티티 컨텍스트별로 쿼리 키가 다르다. keepPreviousData를 쓰면 컨텍스트가 바뀌거나
    // 없어질 때(FAB로 열기) 이전 엔티티의 정책이 placeholder로 새어나와 정책 블록이 잘못 노출된다.
  });

const usePolicyMutationInvalidation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => queryClient.invalidateQueries({ queryKey: queryKeys.runnerDispatchPolicies.all(projectId) }),
    token,
    projectId,
  };
};

export const useCreateRunnerDispatchPolicyMutation = (token: string | null, projectId: string) => {
  const { invalidate, token: authToken, projectId: targetProjectId } = usePolicyMutationInvalidation(token, projectId);
  return useMutation({
    mutationFn: (input: CreateRunnerDispatchPolicyInput) =>
      createRunnerDispatchPolicy(authToken as string, targetProjectId, input),
    onSettled: () => void invalidate(),
  });
};

export const useUpdateRunnerDispatchPolicyMutation = (token: string | null, projectId: string) => {
  const { invalidate, token: authToken, projectId: targetProjectId } = usePolicyMutationInvalidation(token, projectId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRunnerDispatchPolicyInput }) =>
      updateRunnerDispatchPolicy(authToken as string, targetProjectId, id, input),
    onSettled: () => void invalidate(),
  });
};

export const useReorderRunnerDispatchPoliciesMutation = (token: string | null, projectId: string) => {
  const { invalidate, token: authToken, projectId: targetProjectId } = usePolicyMutationInvalidation(token, projectId);
  return useMutation({
    mutationFn: (ids: string[]) => reorderRunnerDispatchPolicies(authToken as string, targetProjectId, ids),
    onSettled: () => void invalidate(),
  });
};

export const useDeleteRunnerDispatchPolicyMutation = (token: string | null, projectId: string) => {
  const { invalidate, token: authToken, projectId: targetProjectId } = usePolicyMutationInvalidation(token, projectId);
  return useMutation({
    mutationFn: (id: string) => deleteRunnerDispatchPolicy(authToken as string, targetProjectId, id),
    onSettled: () => void invalidate(),
  });
};

export const useCreateRunnerDispatchPolicyPresetMutation = (token: string | null, projectId: string) => {
  const { invalidate, token: authToken, projectId: targetProjectId } = usePolicyMutationInvalidation(token, projectId);
  return useMutation({
    mutationFn: (input: CreateRunnerDispatchPolicyPresetInput) =>
      createRunnerDispatchPolicyPreset(authToken as string, targetProjectId, input),
    onSettled: () => void invalidate(),
  });
};

export const useUpdateRunnerDispatchPolicyPresetMutation = (token: string | null, projectId: string) => {
  const { invalidate, token: authToken, projectId: targetProjectId } = usePolicyMutationInvalidation(token, projectId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRunnerDispatchPolicyPresetInput }) =>
      updateRunnerDispatchPolicyPreset(authToken as string, targetProjectId, id, input),
    onSettled: () => void invalidate(),
  });
};

export const useDeleteRunnerDispatchPolicyPresetMutation = (token: string | null, projectId: string) => {
  const { invalidate, token: authToken, projectId: targetProjectId } = usePolicyMutationInvalidation(token, projectId);
  return useMutation({
    mutationFn: (id: string) => deleteRunnerDispatchPolicyPreset(authToken as string, targetProjectId, id),
    onSettled: () => void invalidate(),
  });
};
